import { transfer } from 'comlink';
import { logGapDiagnostic } from './GapDiagnostics';
const LOG_PREFIX = '[Track]';
export const DEFAULT_MAX_BUFFERED_FRAMES = 3;
const SAFETY_CAP = 100;
export class Track {
    trackId;
    local;
    listener;
    transferLoggedOnce = false;
    buffer = [];
    isProcessing = false;
    constructor(trackId, isLocal) {
        this.trackId = trackId;
        this.local = isLocal;
    }
    getTrackId() {
        return Promise.resolve(this.trackId);
    }
    isLocal() {
        return Promise.resolve(this.local);
    }
    setListener(listener) {
        this.listener = listener;
        if (this.buffer.length > 0) {
            void this.processBuffer();
        }
    }
    unsetListener() {
        this.listener = undefined;
    }
    /**
     * Add a frame to the buffer and start async processing.
     * Returns the current buffer length.
     *
     * Producers should check getBufferLength() before calling emit()
     * and handle drops themselves (analogous to DataChannel.bufferedAmount).
     */
    emit(frame, done, transferSet = []) {
        if (!this.listener && this.buffer.length === 0) {
            for (const t of transferSet) {
                if (t instanceof VideoFrame) {
                    t.close();
                }
            }
            return 0;
        }
        this.buffer.push({ frame, done, transferSet });
        if (this.buffer.length > SAFETY_CAP) {
            const old = this.buffer.shift();
            console.error(`${LOG_PREFIX} safety cap exceeded, discarding oldest item`, {
                trackId: this.trackId,
                bufferLength: this.buffer.length,
            });
            logGapDiagnostic('track.safetyCapDrop', {
                trackId: this.trackId,
                detail: {
                    bufferLength: this.buffer.length,
                },
            });
            if (old?.transferSet) {
                for (const t of old.transferSet) {
                    if (t instanceof VideoFrame) {
                        t.close();
                    }
                }
            }
        }
        void this.processBuffer();
        return this.buffer.length;
    }
    /**
     * Process buffered items one-by-one, awaiting each listener call.
     */
    async processBuffer() {
        // Prevent concurrent processing
        if (this.isProcessing || !this.listener) {
            return;
        }
        this.isProcessing = true;
        try {
            while (this.buffer.length > 0 && this.listener) {
                const item = this.buffer.shift();
                if (!item) {
                    continue;
                }
                if (item.transferSet.length > 0 && item.frame !== undefined) {
                    // Use Comlink's transfer() to mark transferables on the frame.
                    // This ensures VideoFrame/ArrayBuffer objects are transferred (moved)
                    // rather than structured-cloned when crossing worker boundaries.
                    //
                    // This is safe for ALL listener types:
                    // - Comlink proxy listeners: Comlink reads the marker and includes
                    //   transferables in postMessage (fast, zero-copy transfer)
                    // - Local listeners: the marker is a harmless Symbol property
                    //
                    // Previously this used isComlinkProxy() to detect Comlink proxies,
                    // but that check relies on Function.prototype.toString containing
                    // "native code" for Proxy objects — which is true on Chrome but NOT
                    // on Safari (where Proxy toString returns the target function's string).
                    if (!this.transferLoggedOnce) {
                        console.log(`${LOG_PREFIX} using transfer for frame with transferables`, {
                            trackId: this.trackId,
                            transferableCount: item.transferSet.length,
                        });
                        this.transferLoggedOnce = true;
                    }
                    await this.listener(transfer(item.frame, item.transferSet), item.done);
                }
                else {
                    await this.listener(item.frame, item.done);
                }
            }
        }
        finally {
            this.isProcessing = false;
        }
    }
    hasListener() {
        return this.listener !== undefined;
    }
    getBufferLength() {
        return this.buffer.length;
    }
}
